import { createContext, useState, useEffect, useContext } from "react"
import { useGlobalContext, decryptUsername } from "./GlobalContext"

const LeaderboardContext = createContext()

export const LeaderboardProviderJiliGrandSlot = ({ children }) => {
  const { username } = useGlobalContext()
  const [leaderboardData, setLeaderboardData] = useState([])
  const [loadingBoard, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [playerPosition, setPlayerPosition] = useState(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [boardMode, setBoardMode] = useState("spinMaster")
  const DEBUG = false

  useEffect(() => {
    const loggedInState = localStorage.getItem("isLoggedIn") === "true"
    setIsLoggedIn(loggedInState)
  }, [])

  const getBonusByRankSpinMaster = (rank) => {
    const r = parseInt(rank, 10)
    if (r === 1) return 200
    if (r === 2) return 120
    if (r === 3) return 100
    if (r >= 4 && r <= 10) return 80
    if (r >= 11 && r <= 20) return 60
    if (r >= 21 && r <= 30) return 50
    return 0
  }

  const maskUsernameExactKeepLast = (u) => {
    if (!u) return u

    const normalized = (u + "###########").slice(0, 11)
    const lastChar = u.slice(-2)

    return normalized.slice(0, 5) + "**" + normalized.charAt(7) + "*" + lastChar
  }

  const getBonusByRankGrandTurnover = (rank) => {
    const r = parseInt(rank, 10)
    if (r === 1) return 5000
    if (r === 2) return 4000
    if (r === 3) return 3000
    if (r === 4) return 2000
    if (r === 5) return 1500
    if (r >= 6 && r <= 10) return 1200
    if (r >= 11 && r <= 20) return 800
    if (r >= 21 && r <= 30) return 500
    return 0
  }

  const getCurrentBoardMode = () => {
    const now = new Date()

    const spinMasterStart = new Date("2026-05-04T00:00:00")
    const spinMasterEnd = new Date("2026-05-18T12:00:00")

    const grandTurnoverStart = new Date("2026-05-18T12:00:00")
    const grandTurnoverEnd = new Date("2026-06-01T12:00:00")

    if (now >= spinMasterStart && now < spinMasterEnd) {
      return "spinMaster"
    }

    if (now >= grandTurnoverStart && now < grandTurnoverEnd) {
      return "grandTurnover"
    }

    return null
  }

  const getBonusByRank = (rank) => {
    const mode = getCurrentBoardMode()

    if (mode === "spinMaster") return getBonusByRankSpinMaster(rank)
    if (mode === "grandTurnover") return getBonusByRankGrandTurnover(rank)

    return 0
  }

  const getEventLabel = () => {
    const mode = getCurrentBoardMode()

    if (mode === "spinMaster")
      return "Spin Masters Challenge: May 4 – May 18 (12PM)"
    if (mode === "grandTurnover")
      return "Grand Turnover Race: May 18 – June 01 (12PM)"

    return null
  }

  const getPlayerPosition = () => {
    const storedUsername = decryptUsername(localStorage.getItem("username"))
    if (!storedUsername) return null

    const found = leaderboardData.find(
      (entry) => entry.randomChars === storedUsername
    )

    return found ? parseInt(found.ranking, 10) : null
  }

  useEffect(() => {
    const pos = getPlayerPosition()
    if (pos !== null) setPlayerPosition(pos)
  }, [leaderboardData])

  useEffect(() => {
    const currentMode = getCurrentBoardMode()
    setBoardMode(currentMode)
  }, [])

  useEffect(() => {
    if (!isLoggedIn) return

    const fetchLeaderboardData = async () => {
      try {
        setLoading(true)

        const currentMode = getCurrentBoardMode()
        if (!currentMode) {
          setLeaderboardData([])
          return
        }

        const cmd =
          currentMode === "spinMaster"
            ? "getJILISpinCountLeaderboard"
            : "getJILITOLeaderboard"

        const url = `${process.env.REACT_APP_API_JSON_URL_PROMO}?cmd=${cmd}`
        const response = await fetch(url)

        if (!response.ok) throw new Error(`HTTP error: ${response.status}`)

        const rawData = await response.json()

        const parsedData = Object.entries(rawData).map(([ranking, value]) => {
          const parts = value.split("|")
          const realUsername = parts[0]?.trim() || ""
          return {
            ranking,
            username,
            bonus: getBonusByRank(ranking),
            randomChars: realUsername,
            masked: realUsername,
          }
        })

        setLeaderboardData(parsedData.slice(0, 30))
      } catch (err) {
        console.error("Leaderboard API error:", err)
        setError(err)
      } finally {
        setLoading(false)
      }
    }

    fetchLeaderboardData()
  }, [isLoggedIn])

  return (
    <LeaderboardContext.Provider
      value={{
        leaderboardData,
        loadingBoard,
        error,
        getBonusByRank,
        getBonusByRankSpinMaster,
        getBonusByRankGrandTurnover,
        getPlayerPosition,
        playerPosition,
        boardMode,
        setBoardMode,
        getEventLabel,
      }}
    >
      {children}
    </LeaderboardContext.Provider>
  )
}

export const useLeaderboard = () => useContext(LeaderboardContext)
